import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle, MessageCircle, ArrowRight, Star, Sparkles } from 'lucide-react';
import { mediaUrl } from '@/components/ImageUploadButton';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const PartnerMinisitePage = () => {
  const { refCode } = useParams();
  const [ms, setMs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${BACKEND}/api/public/minisite/${refCode}`)
      .then((r) => setMs(r.data))
      .catch((e) => setError(e?.response?.data?.detail || 'Página não encontrada'))
      .finally(() => setLoading(false));
  }, [refCode]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Carregando...</div>;
  }
  if (error || !ms) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-8">
        <p className="text-2xl font-bold text-slate-800 mb-2">Página não encontrada</p>
        <p className="text-slate-500 mb-4">{error}</p>
        <Link to="/" className="text-emerald-600 hover:underline">Voltar para a página inicial</Link>
      </div>
    );
  }

  const primary = ms.primary_color || '#059669';
  const signupUrl = `/?ref=${ms.ref_code}#signup`;
  const canonical = `https://nfcesys.com.br/p/${ms.ref_code}`;
  const waHref = ms.whatsapp_contact
    ? `https://wa.me/${String(ms.whatsapp_contact).replace(/\D/g, '')}?text=Ol%C3%A1%21%20Vim%20pelo%20parceiro%20${encodeURIComponent(ms.partner_name)}%20e%20quero%20saber%20mais%20sobre%20o%20nfcesysPdv`
    : null;

  // JSON-LD Schema.org
  const schemaJsonLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "nfcesysPdv",
    description: ms.description,
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web, Android, iOS",
    offers: {
      "@type": "Offer",
      price: "49.90",
      priceCurrency: "BRL",
    },
    provider: {
      "@type": "Organization",
      name: `nfcesysPdv — Parceiro ${ms.partner_name}`,
      url: canonical,
    },
    aggregateRating: (ms.testimonials || []).length > 0 ? {
      "@type": "AggregateRating",
      ratingValue: "4.9",
      reviewCount: String((ms.testimonials || []).length + 50),
    } : undefined,
  };

  return (
    <div className="min-h-screen bg-white" data-testid="minisite-page">
      {/* SEO tags */}
      <Helmet>
        <title>{ms.title}</title>
        <meta name="description" content={ms.description} />
        <meta name="keywords" content={ms.keywords} />
        <link rel="canonical" href={canonical} />

        {/* Open Graph */}
        <meta property="og:title" content={ms.title} />
        <meta property="og:description" content={ms.description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        {ms.hero_image_url && <meta property="og:image" content={ms.hero_image_url} />}
        <meta property="og:locale" content="pt_BR" />
        <meta property="og:site_name" content={`nfcesysPdv — ${ms.partner_name}`} />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={ms.title} />
        <meta name="twitter:description" content={ms.description} />
        {ms.hero_image_url && <meta name="twitter:image" content={ms.hero_image_url} />}

        {/* JSON-LD */}
        <script type="application/ld+json">{JSON.stringify(schemaJsonLd)}</script>
      </Helmet>

      {/* Header */}
      <header className="border-b bg-white sticky top-0 z-40" style={{ borderColor: '#e2e8f0' }}>
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {ms.logo_url ? (
              <img src={ms.logo_url} alt={`Logo ${ms.partner_name}`} className="h-10 object-contain" />
            ) : (
              <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold" style={{ backgroundColor: primary }}>
                <Sparkles className="w-5 h-5" />
              </div>
            )}
            <div>
              <p className="font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>nfcesysPdv</p>
              <p className="text-xs text-slate-500">Parceiro oficial: <strong>{ms.partner_name}</strong></p>
            </div>
          </div>
          <a href={signupUrl}>
            <Button className="text-white shadow-md" style={{ backgroundColor: primary }} data-testid="cta-header">
              {ms.cta_text} <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </a>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 py-16 grid md:grid-cols-2 gap-10 items-center">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mb-4" style={{ backgroundColor: `${primary}22`, color: primary }}>
            <Sparkles className="w-3 h-3" /> Parceiro Verificado
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4 leading-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {ms.hero_headline}
          </h1>
          <p className="text-lg text-slate-600 mb-8">{ms.hero_body}</p>
          <div className="flex flex-wrap gap-3">
            <a href={signupUrl}>
              <Button size="lg" className="text-white text-base h-14 px-8" style={{ backgroundColor: primary }} data-testid="cta-hero">
                {ms.cta_text} <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </a>
            {waHref && (
              <a href={waHref} target="_blank" rel="noopener noreferrer">
                <Button size="lg" variant="outline" className="text-base h-14 px-8" data-testid="cta-whatsapp">
                  <MessageCircle className="mr-2 w-5 h-5 text-emerald-600" /> Falar no WhatsApp
                </Button>
              </a>
            )}
          </div>
          <p className="text-xs text-slate-400 mt-4">
            30 dias grátis • Sem cartão de crédito • Suporte por WhatsApp
          </p>
        </div>
        <div>
          {ms.hero_image_url ? (
            <img src={ms.hero_image_url} alt={ms.hero_headline} className="w-full rounded-2xl shadow-2xl" />
          ) : (
            <div className="w-full aspect-square rounded-2xl shadow-2xl flex items-center justify-center text-white text-9xl font-bold" style={{ background: `linear-gradient(135deg, ${primary}, ${primary}cc)` }}>
              <Sparkles className="w-32 h-32 opacity-70" />
            </div>
          )}
        </div>
      </section>

      {/* Benefícios */}
      {(ms.benefits || []).length > 0 && (
        <section className="bg-slate-50 py-16">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-center mb-2 text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              O que o nfcesysPdv faz por você
            </h2>
            <p className="text-center text-slate-500 mb-10">Um sistema, todas as ferramentas para vender mais</p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {ms.benefits.map((b, i) => (
                <Card key={i} className="border-slate-200 hover:shadow-lg transition-shadow" data-testid={`benefit-${i}`}>
                  <CardContent className="pt-6">
                    <CheckCircle className="w-8 h-8 mb-3" style={{ color: primary }} />
                    <h3 className="font-bold text-slate-900 mb-2">{b.title}</h3>
                    <p className="text-sm text-slate-600">{b.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Vídeo */}
      {ms.video_url && (
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-6">
            <div className="aspect-video rounded-2xl overflow-hidden shadow-2xl">
              <iframe
                src={ms.video_url}
                title="Vídeo apresentação"
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </section>
      )}

      {/* Depoimentos */}
      {(ms.testimonials || []).length > 0 && (
        <section className="py-16">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-center mb-10 text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Quem já usa está falando
            </h2>
            <div className="grid md:grid-cols-3 gap-4">
              {ms.testimonials.map((t, i) => (
                <Card key={i} data-testid={`testimonial-${i}`}>
                  <CardContent className="pt-6">
                    <div className="flex gap-1 mb-3" style={{ color: primary }}>
                      {[1, 2, 3, 4, 5].map((s) => <Star key={s} className="w-4 h-4 fill-current" />)}
                    </div>
                    <p className="text-slate-700 italic mb-4">&ldquo;{t.text}&rdquo;</p>
                    <div className="flex items-center gap-3">
                      {t.logo_url && <img src={mediaUrl(t.logo_url)} alt={t.business} className="w-10 h-10 rounded-full object-cover border bg-white" data-testid={`testimonial-logo-${i}`} />}
                      <div>
                        <p className="font-semibold text-slate-900">{t.name}</p>
                        <p className="text-sm text-slate-500">{t.business}{t.city ? ` • ${t.city}` : ''}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA final */}
      <section className="py-20" style={{ backgroundColor: primary }}>
        <div className="max-w-4xl mx-auto px-6 text-center text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Comece agora sem risco
          </h2>
          <p className="text-lg mb-8 opacity-90">30 dias grátis • Sem cartão • Cancela quando quiser</p>
          <a href={signupUrl}>
            <Button size="lg" className="bg-white text-slate-900 hover:bg-slate-100 text-base h-14 px-10 shadow-xl" data-testid="cta-final">
              {ms.cta_text} <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </a>
          <p className="text-sm mt-6 opacity-75">
            Indicado por <strong>{ms.partner_name}</strong> — Parceiro oficial nfcesysPdv
          </p>
        </div>
      </section>

      {/* Rodapé */}
      <footer className="border-t bg-white py-8 text-center text-sm text-slate-500">
        <p>© {new Date().getFullYear()} nfcesysPdv — Todos os direitos reservados</p>
        <p className="mt-1">Página do parceiro <strong>{ms.partner_name}</strong> (código: <code className="bg-slate-100 px-1 rounded">{ms.ref_code}</code>)</p>
      </footer>
    </div>
  );
};

export default PartnerMinisitePage;
